import {
  AccountStatus,
  ConnectionCategory,
} from '@finch-api/common/dist/external/dashboard/connection-status';
import uniqBy from 'lodash/uniqBy';
import { Path } from 'react-hook-form';
import {
  ConnectionsCsvHeader,
  ConnectionsTableColumn,
  IncompleteConnectionsCsvHeader,
  StagedConnection,
} from './types';
import { Duration } from 'luxon';

export const connectionStatusOptions = [
  {
    id: AccountStatus.CONNECTED,
    label: 'Connected',
  },
  {
    id: AccountStatus.REAUTH,
    label: 'Reauthentication Needed',
  },
  {
    id: AccountStatus.PENDING,
    label: 'Pending',
  },
  {
    id: AccountStatus.ERROR_NO_ACCOUNT_SETUP,
    label: 'No Account Set Up',
  },
  {
    id: AccountStatus.ERROR_PERMISSIONS,
    label: 'Insufficient Permissions',
  },
];

export const connectStepOptions = [
  {
    id: 'PREAMBLE',
    label: 'Terms of service',
  },
  {
    id: 'PERMISSIONS',
    label: 'Permissions',
  },
  {
    id: 'SELECT_PROVIDER',
    label: 'Provider Selection',
  },
  {
    id: 'AUTH', // New combined ID for authorization steps
    label: 'Authorization',
    steps: ['SIGN_IN', 'MFA', 'CAPTCHA', 'CHOOSE_ACCOUNT'],
  },
  {
    id: 'NOT_STARTED',
    label: 'Not Started',
  },
  {
    id: 'EXPIRED',
    label: 'Session Expired',
  },
  {
    id: 'COMPLETED',
    label: 'Session Completed',
  },
];

export const connectionTypeOptions = [
  {
    id: [ConnectionCategory.Automated],
    label: 'Automated only',
  },
  {
    id: [ConnectionCategory.Assisted],
    label: 'Assisted only',
  },
  {
    id: [ConnectionCategory.Automated, ConnectionCategory.Assisted],
    label: 'Automated and Assisted',
  },
];

export const connectionProductsOptions = [
  {
    id: ['Organization'],
    label: 'Organization',
  },
  {
    id: ['Organization', 'Payroll'],
    label: 'Organization and Payroll',
  },
  {
    id: ['Organization', 'Payroll', 'Deductions'],
    label: 'Organization, Payroll and Deductions',
  },
  {
    id: ['Organization', 'Payroll', 'Deductions', 'Documents'],
    label: 'Organization, Payroll, Deductions and Documents',
  },
];

export const getProviderOptions = (
  tableData: (ConnectionsTableColumn | StagedConnection)[],
) =>
  uniqBy(
    tableData.map((data) => ({
      id: 'provider' in data ? data.provider.name : data.providerName,
      label: 'provider' in data ? data.provider.name : data.providerName,
    })),
    'id',
  ).sort((a, b) => a.label?.localeCompare(b?.label ?? '') ?? 0);

export const connectionsFilterList = [
  'companyName',
  'connectionId',
  'companyId',
  'status',
  'kinds',
  'connectedAt',
  'lastSyncAt',
  'products',
  'providerName',
] as const;

export const stagedConnectionFilterList = [
  'externalCompanyId',
  'externalCompanyName',
  'providerName',
  'firstConnectedAt',
  'latestStepInfo',
] as const;

export const CsvToConnectionTableMap: Record<
  ConnectionsCsvHeader,
  Path<ConnectionsTableColumn>
> = {
  'Connection ID': 'connectionId',
  'Company ID': 'companyId',
  'Company Name': 'companyName',
  'Customer ID': 'customer.id',
  'Customer Name': 'customer.name',
  'Connection Status': 'status',
  'Connection Type': 'kinds',
  'Provider ID': 'provider.id',
  'Provider Name': 'providerName',
  'First Connected At': 'connectedAtIso',
  'Last Synced At': 'lastSyncAtIso',
  Products: 'products',
  'Disconnected At': 'updatedAtIso',
};

export const CsvToIncompleteConnectionTableMap: Record<
  IncompleteConnectionsCsvHeader,
  Path<StagedConnection>
> = {
  'Customer ID': 'externalCompanyId',
  'Customer Name': 'externalCompanyName',
  'Created At': 'firstConnectedAt',
  'Provider ID': 'providerId',
  'Provider Name': 'providerName',
  'Stage Completed': 'latestStepInfo.step',
};

export const DisabledTableStyles = {
  opacity: 0.5,
  pointerEvents: 'none',
};

export const CONNECT_SESSION_EXPIRY_MIN = Duration.fromObject({ days: 30 }).as(
  'minutes',
);
